import { extname } from './helpers';
import { normalizeContentType } from './mime-types';

/**
 * Content types accepted by OpenAI as file inputs (everything except images, which are handled
 * as a distinct input kind). Mirrors the list documented at
 * https://developers.openai.com/api/docs/guides/file-inputs — keep it in sync when OpenAI updates
 * the supported set.
 */
export const OPENAI_SUPPORTED_FILE_MIME_TYPES = [
    // PDF
    'application/pdf',

    // Spreadsheets
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'application/csv',
    'text/tsv',
    'text/x-iif',
    'application/x-iif',
    'application/vnd.google-apps.spreadsheet',

    // Rich documents
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/rtf',
    'text/rtf',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.apple.pages',
    'application/vnd.google-apps.document',
    'application/vnd.apple.iwork',

    // Presentations
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-powerpoint',
    'application/vnd.apple.keynote',
    'application/vnd.google-apps.presentation',

    // Text and code
    'application/javascript',
    'application/typescript',
    'text/xml',
    'text/x-shellscript',
    'text/x-rst',
    'text/x-makefile',
    'text/x-lisp',
    'text/x-asm',
    'text/vbscript',
    'text/css',
    'message/rfc822',
    'application/x-sql',
    'application/x-scala',
    'application/x-rust',
    'application/x-powershell',
    'text/x-diff',
    'text/x-patch',
    'application/x-patch',
    'text/plain',
    'text/markdown',
    'text/x-java',
    'text/x-script.python',
    'text/x-python',
    'text/x-c',
    'text/x-c++',
    'text/x-golang',
    'text/html',
    'text/x-php',
    'application/x-php',
    'application/x-httpd-php',
    'application/x-httpd-php-source',
    'text/x-ruby',
    'text/x-sh',
    'text/x-bash',
    'application/x-bash',
    'text/x-zsh',
    'text/x-tex',
    'text/x-csharp',
    'application/json',
    'text/x-typescript',
    'text/javascript',
    'text/x-go',
    'text/x-rust',
    'text/x-scala',
    'text/x-kotlin',
    'text/x-swift',
    'text/x-lua',
    'text/x-r',
    'text/x-R',
    'text/x-julia',
    'text/x-perl',
    'text/x-objectivec',
    'text/x-objectivec++',
    'text/x-erlang',
    'text/x-elixir',
    'text/x-haskell',
    'text/x-clojure',
    'text/x-groovy',
    'text/x-dart',
    'text/x-awk',
    'application/x-awk',
    'text/jsx',
    'text/tsx',
    'text/x-handlebars',
    'text/x-mustache',
    'text/x-ejs',
    'text/x-jinja2',
    'text/x-liquid',
    'text/x-erb',
    'text/x-twig',
    'text/x-pug',
    'text/x-jade',
    'text/x-tmpl',
    'text/x-cmake',
    'text/x-dockerfile',
    'text/x-gradle',
    'text/x-ini',
    'text/x-properties',
    'text/x-protobuf',
    'application/x-protobuf',
    'text/x-sql',
    'text/x-sass',
    'text/x-scss',
    'text/x-less',
    'text/x-hcl',
    'text/x-terraform',
    'application/x-terraform',
    'text/x-toml',
    'application/x-toml',
    'application/graphql',
    'application/x-graphql',
    'text/x-graphql',
    'application/x-ndjson',
    'application/json5',
    'application/x-json5',
    'text/x-yaml',
    'application/toml',
    'application/x-yaml',
    'application/yaml',
    'text/x-astro',
    'text/srt',
    'application/x-subrip',
    'text/x-subrip',
    'text/vtt',
    'text/x-vcard',
    'text/calendar',
] as const;

const OPENAI_SUPPORTED_FILE_MIME_TYPES_SET = new Set<string>(OPENAI_SUPPORTED_FILE_MIME_TYPES);

/**
 * Extension → content type fallback used when the browser reports no (or a generic) MIME type,
 * which is common for code and text files. Each target content type is a member of
 * {@link OPENAI_SUPPORTED_FILE_MIME_TYPES} so the resolved type is always one OpenAI accepts.
 */
const OPENAI_FILE_EXTENSION_TO_MIME: Record<string, string> = {
    // Documents
    '.pdf': 'application/pdf',
    '.doc': 'application/msword',
    '.dot': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.odt': 'application/vnd.oasis.opendocument.text',
    '.rtf': 'application/rtf',

    // Spreadsheets
    '.xls': 'application/vnd.ms-excel',
    '.xlt': 'application/vnd.ms-excel',
    '.xla': 'application/vnd.ms-excel',
    '.xlb': 'application/vnd.ms-excel',
    '.xlc': 'application/vnd.ms-excel',
    '.xlm': 'application/vnd.ms-excel',
    '.xlw': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.csv': 'text/csv',
    '.tsv': 'text/tsv',
    '.iif': 'text/x-iif',

    // Presentations
    '.ppt': 'application/vnd.ms-powerpoint',
    '.pot': 'application/vnd.ms-powerpoint',
    '.ppa': 'application/vnd.ms-powerpoint',
    '.pps': 'application/vnd.ms-powerpoint',
    '.pwz': 'application/vnd.ms-powerpoint',
    '.wiz': 'application/vnd.ms-powerpoint',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

    // Text and code
    '.txt': 'text/plain',
    '.text': 'text/plain',
    '.log': 'text/plain',
    '.conf': 'text/plain',
    '.md': 'text/markdown',
    '.markdown': 'text/markdown',
    '.json': 'application/json',
    '.json5': 'application/json5',
    '.jsonl': 'application/x-ndjson',
    '.ndjson': 'application/x-ndjson',
    '.xml': 'text/xml',
    '.html': 'text/html',
    '.htm': 'text/html',
    '.css': 'text/css',
    '.sass': 'text/x-sass',
    '.scss': 'text/x-scss',
    '.less': 'text/x-less',
    '.js': 'text/javascript',
    '.mjs': 'text/javascript',
    '.cjs': 'text/javascript',
    '.jsx': 'text/jsx',
    '.ts': 'text/x-typescript',
    '.tsx': 'text/tsx',
    '.py': 'text/x-python',
    '.c': 'text/x-c',
    '.h': 'text/x-c',
    '.cc': 'text/x-c++',
    '.cpp': 'text/x-c++',
    '.cxx': 'text/x-c++',
    '.hh': 'text/x-c++',
    '.hpp': 'text/x-c++',
    '.cs': 'text/x-csharp',
    '.java': 'text/x-java',
    '.go': 'text/x-golang',
    '.rs': 'application/x-rust',
    '.rb': 'text/x-ruby',
    '.erb': 'text/x-erb',
    '.php': 'text/x-php',
    '.swift': 'text/x-swift',
    '.kt': 'text/x-kotlin',
    '.kts': 'text/x-kotlin',
    '.lua': 'text/x-lua',
    '.r': 'text/x-r',
    '.jl': 'text/x-julia',
    '.pl': 'text/x-perl',
    '.pm': 'text/x-perl',
    '.scala': 'application/x-scala',
    '.sh': 'text/x-sh',
    '.bash': 'text/x-bash',
    '.zsh': 'text/x-zsh',
    '.ksh': 'text/x-sh',
    '.bat': 'text/x-shellscript',
    '.ps1': 'application/x-powershell',
    '.sql': 'application/x-sql',
    '.m': 'text/x-objectivec',
    '.mm': 'text/x-objectivec++',
    '.erl': 'text/x-erlang',
    '.ex': 'text/x-elixir',
    '.exs': 'text/x-elixir',
    '.hs': 'text/x-haskell',
    '.clj': 'text/x-clojure',
    '.groovy': 'text/x-groovy',
    '.dart': 'text/x-dart',
    '.awk': 'text/x-awk',
    '.yaml': 'application/yaml',
    '.yml': 'application/yaml',
    '.toml': 'application/toml',
    '.ini': 'text/x-ini',
    '.cfg': 'text/x-ini',
    '.properties': 'text/x-properties',
    '.proto': 'text/x-protobuf',
    '.graphql': 'application/graphql',
    '.gql': 'application/graphql',
    '.hcl': 'text/x-hcl',
    '.tf': 'text/x-terraform',
    '.diff': 'text/x-diff',
    '.patch': 'text/x-patch',
    '.rst': 'text/x-rst',
    '.tex': 'text/x-tex',
    '.astro': 'text/x-astro',
    '.s': 'text/x-asm',
    '.asm': 'text/x-asm',
    '.cmake': 'text/x-cmake',
    '.gradle': 'text/x-gradle',
    '.hbs': 'text/x-handlebars',
    '.handlebars': 'text/x-handlebars',
    '.mustache': 'text/x-mustache',
    '.ejs': 'text/x-ejs',
    '.jinja': 'text/x-jinja2',
    '.jinja2': 'text/x-jinja2',
    '.j2': 'text/x-jinja2',
    '.liquid': 'text/x-liquid',
    '.twig': 'text/x-twig',
    '.pug': 'text/x-pug',
    '.jade': 'text/x-jade',
    '.vbs': 'text/vbscript',
    '.lisp': 'text/x-lisp',
    '.el': 'text/x-lisp',
    '.eml': 'message/rfc822',
    '.mht': 'message/rfc822',
    '.mhtml': 'message/rfc822',
    '.srt': 'text/srt',
    '.vtt': 'text/vtt',
    '.vcf': 'text/x-vcard',
    '.ics': 'text/calendar',
    '.ifb': 'text/calendar',
};

/**
 * File extensions accepted as OpenAI file inputs, derived from the extension → content type map.
 * Useful for building an `accept` attribute on a file picker.
 */
export const OPENAI_SUPPORTED_FILE_EXTENSIONS = Object.keys(OPENAI_FILE_EXTENSION_TO_MIME);

/**
 * Whether the given content type is accepted by OpenAI as a file input (charset is ignored).
 */
export function isOpenAISupportedFileContentType(contentType: string | null | undefined): boolean {
    if (!contentType) {
        return false;
    }
    try {
        return OPENAI_SUPPORTED_FILE_MIME_TYPES_SET.has(normalizeContentType(contentType));
    } catch {
        return false;
    }
}

/**
 * Resolve the content type to send to OpenAI for a file, or `null` when the file is not a supported
 * file input. Prefers the browser-reported MIME type, falling back to the extension when it is
 * missing or not recognized — browsers often report an empty or generic type for code/text files.
 */
export function getOpenAISupportedFileContentType(file: {
    name: string;
    type?: string | null;
}): string | null {
    if (file.type) {
        try {
            const normalized = normalizeContentType(file.type);
            if (OPENAI_SUPPORTED_FILE_MIME_TYPES_SET.has(normalized)) {
                return normalized;
            }
        } catch {
            // Ignore an unparseable content type and fall back to the extension.
        }
    }

    return OPENAI_FILE_EXTENSION_TO_MIME[extname(file.name)] ?? null;
}

/**
 * Whether the given file is accepted by OpenAI as a file input, matching on either its MIME type or
 * its extension.
 */
export function isOpenAISupportedFile(file: { name: string; type?: string | null }): boolean {
    return getOpenAISupportedFileContentType(file) !== null;
}
